/**
 * Ownership service. Replaces:
 *   - `ownership-gov.js::getLastOwnershipForPlate`
 *   - `ownership-gov.js::persistOwnershipDates`
 *   - `ownership-gov.js::ensureGovBundle`
 *   - `ownership-gov.js::checkOwnershipChangeAndNotify`
 *
 * The legacy file defined `toISOFromBaalut` twice and re-defined date helpers
 * inside `checkOwnershipChangeAndNotify`. Here we use the single
 * `shared/date.js` utilities throughout.
 */

import { ref } from '../../core/firebase.js';
import { fetchOwnershipHistory, findVehicle } from '../../data/apis/ckan.js';
import { parseBaalutToISO, isoToYearMonth, quarterEndISO } from '../../shared/date.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { Auth } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';
import { getDeviceId } from '../auth/device.service.js';

const log = createLogger('ownership');

/**
 * @typedef {Object} OwnershipRecord
 * @property {string | null} dateISO     Most recent baalut_dt as ISO UTC.
 * @property {string | null} quarterEnd  End-of-quarter ISO for that date.
 * @property {string | null} yearMonth   `YYYY-MM` form of `dateISO`.
 * @property {any | null} raw            Original gov record (for debugging).
 */

/** @type {Map<string, Promise<OwnershipRecord>>} */
const inflight = new Map();

/** 24h TTL for ownership data — mirrors the license/gov-bundle cache.
 *  Ownership-of-record changes very rarely (yearly at most), so a day-old
 *  value is safely accurate; the trade-off is a much snappier "My Car"
 *  panel since the banner doesn't wait on a CKAN roundtrip every open. */
const OWNERSHIP_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Fetch the most recent ownership record for a plate. Dedupes concurrent
 * requests for the same plate so multiple callers share one network round.
 *
 * Caching: looks first at `vehicles/{plate}/lastOwnership(*)` stamped by a
 * previous `persistOwnership` call. If the stamp is fresher than the TTL
 * we return the cached record (no CKAN call). Otherwise we fetch fresh
 * and re-stamp so the next caller hits the cache.
 *
 * Pass `maxAgeMs = 0` to force a fresh fetch (used by deliberate refresh
 * jobs); the default `OWNERSHIP_TTL_MS` is what view-layer callers want.
 *
 * @param {string} plate
 * @param {number} [maxAgeMs]
 * @returns {Promise<OwnershipRecord>}
 */
export function getLastOwnership(plate, maxAgeMs = OWNERSHIP_TTL_MS) {
  const p = normalizePlate(plate);
  if (!p) return Promise.resolve(empty());

  const existing = inflight.get(p);
  if (existing) return existing;

  const promise = (async () => {
    // Cache check first — saves a CKAN roundtrip when the stamp is fresh.
    if (maxAgeMs > 0) {
      try {
        const snap = await ref(`vehicles/${p}`).once('value');
        const v = snap.val() || {};
        const cachedAt = typeof v.lastOwnershipUpdatedAt === 'number' ? v.lastOwnershipUpdatedAt : 0;
        if (v.lastOwnership && Date.now() - cachedAt < maxAgeMs) {
          return {
            dateISO: v.lastOwnership,
            quarterEnd: v.lastOwnershipQuarterEnd ?? null,
            yearMonth: isoToYearMonth(v.lastOwnership),
            raw: null,
          };
        }
      } catch (_) { /* fall through to fresh fetch */ }
    }

    try {
      const history = await fetchOwnershipHistory(p);
      if (history.length === 0) return empty();
      const top = history[0];
      const dateISO = parseBaalutToISO(top?.baalut_dt) ?? null;
      const record = /** @type {OwnershipRecord} */ ({
        dateISO,
        quarterEnd: dateISO ? quarterEndISO(dateISO) : null,
        yearMonth: isoToYearMonth(dateISO),
        raw: top,
      });
      // Re-stamp the cache so future callers can hit it within the TTL.
      if (record.dateISO) {
        try { await persistOwnership(p, '', record); } catch (_) { /* non-fatal */ }
      }
      return record;
    } catch (err) {
      log.warn('getLastOwnership failed for', p, err);
      return empty();
    }
  })();
  inflight.set(p, promise);
  promise.finally(() => inflight.delete(p));
  return promise;
}

/**
 * Check whether a user-entered ownership month matches the gov record.
 * Used by the replace-plate flow to confirm the user actually owns the car.
 *
 * @deprecated The register/replace flow now uses `matchProductionYear` which
 * relies on the main vehicles dataset (covers every vehicle, not just
 * ownership-changed-since-2017). Kept here in case any caller still depends
 * on the old check.
 *
 * @param {string} plate
 * @param {string} userYearMonth  `YYYY-MM`
 * @returns {Promise<{ matches: boolean; govYearMonth: string | null }>}
 */
export async function matchOwnershipMonth(plate, userYearMonth) {
  const record = await getLastOwnership(plate);
  const gov = record.yearMonth;
  if (!gov) return { matches: false, govYearMonth: null };
  return { matches: gov === userYearMonth, govYearMonth: gov };
}

/**
 * Check whether a user-entered PRODUCTION year matches the gov record.
 * Reads `shnat_yitzur` from the main vehicles dataset (a field that exists
 * on every vehicle, unlike `baalut_dt` which only covers post-2017 ownership
 * changes). This is the replacement for `matchOwnershipMonth` in the
 * register/replace flow.
 *
 * Returns:
 *   - `{ matches: true,  govYear }`        — plate found, year matches.
 *   - `{ matches: false, govYear }`        — plate found, year differs.
 *   - `{ matches: false, govYear: null }`  — plate NOT found in gov registry.
 *   - `{ matches: false, govYear: null, down: true }` — CKAN unreachable;
 *     caller may proceed with a warning rather than fail outright.
 *
 * @param {string} plate
 * @param {string} userYear        4-digit string, e.g. "2014"
 * @returns {Promise<{ matches: boolean; govYear: string | null; down?: boolean }>}
 */
export async function matchProductionYear(plate, userYear) {
  const p = normalizePlate(plate);
  const y = String(userYear || '').replace(/\D/g, '');
  if (!p || !/^\d{4}$/.test(y)) {
    return { matches: false, govYear: null };
  }
  try {
    const { findVehicle } = await import('../../data/apis/ckan.js');
    const { records } = await findVehicle(p);
    if (records.length === 0) {
      return { matches: false, govYear: null };
    }
    const govYearRaw = records[0]?.shnat_yitzur;
    const govYear = govYearRaw ? String(govYearRaw).replace(/\D/g, '') : null;
    if (!govYear) return { matches: false, govYear: null };
    return { matches: govYear === y, govYear };
  } catch (err) {
    log.warn('matchProductionYear: gov fetch failed for', p, err);
    return { matches: false, govYear: null, down: true };
  }
}

/**
 * Persist gov-derived ownership metadata onto the vehicle record. Should be
 * called after registration / plate-replace so the banner has a cached value.
 *
 * @param {string} plate
 * @param {string} phone
 * @param {OwnershipRecord | null} record
 */
export async function persistOwnership(plate, phone, record) {
  const p = normalizePlate(plate);
  if (!p || !record?.dateISO) return;
  const now = Date.now();
  const updates = /** @type {Record<string, unknown>} */ ({
    lastOwnership: record.dateISO,
    lastOwnershipQuarterEnd: record.quarterEnd ?? null,
    lastOwnershipUpdatedAt: now,
  });
  await ref(`vehicles/${p}`).update(updates);

  // Also stamp this device record so future watchers can compare timestamps.
  const deviceId = getDeviceId();
  if (deviceId) {
    await ref(`vehicles/${p}/devices/${deviceId}`).update({
      ownershipISO: record.dateISO,
      ownershipUpdatedAt: now,
    });
  }

  // Mirror to localStorage for offline render (matches legacy key name so the
  // existing banner code keeps working until it's replaced).
  try {
    if (record.yearMonth) {
      localStorage.setItem(`lastGovBaalutDt_${p}`, record.yearMonth.replace('-', ''));
    }
  } catch { /* ignore */ }
}

/**
 * Ensure the vehicle's gov bundle (basic record) is cached. Used when the
 * details modal opens — avoids re-fetching if cached recently.
 *
 * @param {string} plate
 * @param {number} [maxAgeMs=24*60*60*1000]
 * @returns {Promise<{ vehicle: any | null; cached: boolean }>}
 */
export async function ensureGovBundle(plate, maxAgeMs = 24 * 60 * 60 * 1000) {
  const p = normalizePlate(plate);
  if (!p) return { vehicle: null, cached: false };

  const cached = (await ref(`vehicles/${p}/gov`).once('value')).val();
  const cachedAt = cached?.vehicleLastSyncMs ?? 0;
  if (cached?.vehicle && Date.now() - cachedAt < maxAgeMs) {
    return { vehicle: cached.vehicle, cached: true };
  }

  const { records } = await findVehicle(p);
  if (records.length === 0) {
    return { vehicle: cached?.vehicle ?? null, cached: false };
  }
  const fresh = records[0];
  await ref(`vehicles/${p}/gov`).update({
    vehicle: fresh,
    vehicleLastSyncMs: Date.now(),
    vehicleLastSyncISO: new Date().toISOString(),
  });
  return { vehicle: fresh, cached: false };
}

/**
 * Read the cached `lastOwnership` ISO from the vehicle record. Faster than
 * a gov roundtrip when only the date string is needed.
 *
 * @param {string} plate
 * @returns {Promise<string | null>}
 */
export async function getStoredOwnershipISO(plate) {
  const p = normalizePlate(plate);
  if (!p) return null;
  const snap = await ref(`vehicles/${p}/lastOwnership`).once('value');
  const v = snap.val();
  return typeof v === 'string' ? v : null;
}

/** Empty result returned when no record is available. */
function empty() {
  return /** @type {OwnershipRecord} */ ({
    dateISO: null,
    quarterEnd: null,
    yearMonth: null,
    raw: null,
  });
}
